import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cookie } from 'lucide-react';
import { LanguageSelector } from '@/components/LanguageSelector';

import {
  OPEN_COOKIE_SETTINGS_EVENT,
  readConsent,
  saveConsent,
} from '@/lib/cookieConsent';

export const CookieConsent = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setOpen(true);
    } else {
      setFunctional(existing.functional);
      setAnalytics(existing.analytics);
    }

    const onOpen = () => {
      const current = readConsent();
      setFunctional(current?.functional ?? false);
      setAnalytics(current?.analytics ?? false);
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen);
  }, []);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    setShowDetails(false);
  };

  const acceptAll = () => {
    saveConsent({ functional: true, analytics: true });
    close();
  };

  const rejectAll = () => {
    saveConsent({ functional: false, analytics: false });
    close();
  };

  const saveChoices = () => {
    saveConsent({ functional, analytics });
    close();
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6" role="dialog" aria-live="polite" aria-label={t('cookies.banner.title')}>
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-[#DBE2EA] p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#EBF4F8] flex items-center justify-center flex-shrink-0">
            <Cookie size={20} className="text-[#0880AE]" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-medium text-[#37699E] font-['Lexend_Deca']">
              {t('cookies.banner.title')}
            </h2>
            <p className="text-sm text-[#2D3748] leading-relaxed">
              {t('cookies.banner.description')}{' '}
              <Link
                to="/cookie-policy"
                className="text-[#0880AE] hover:text-[#076A8E] underline transition-colors duration-200"
              >
                {t('cookies.banner.readPolicy')}
              </Link>
            </p>
          </div>
        </div>

        {showDetails && (
          <div className="space-y-3 border-t border-[#DBE2EA] pt-4">
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-[#F8F9FA]">
              <div>
                <p className="text-sm font-medium text-[#2D3748]">{t('cookies.categories.necessary.title')}</p>
                <p className="text-sm text-[#5A6B7A]">{t('cookies.categories.necessary.description')}</p>
              </div>
              <span className="text-xs font-medium text-[#14A38B] uppercase tracking-wide whitespace-nowrap">
                {t('cookies.alwaysOn')}
              </span>
            </div>

            <label className="flex items-start justify-between gap-4 p-4 rounded-lg bg-[#F8F9FA] cursor-pointer">
              <div>
                <p className="text-sm font-medium text-[#2D3748]">{t('cookies.categories.functional.title')}</p>
                <p className="text-sm text-[#5A6B7A]">{t('cookies.categories.functional.description')}</p>
              </div>
              <input
                type="checkbox"
                checked={functional}
                onChange={(e) => setFunctional(e.target.checked)}
                className="w-4 h-4 mt-1 rounded border-[#DBE2EA] text-[#0880AE] focus:ring-[#0880AE] focus:ring-2"
              />
            </label>

            <label className="flex items-start justify-between gap-4 p-4 rounded-lg bg-[#F8F9FA] cursor-pointer">
              <div>
                <p className="text-sm font-medium text-[#2D3748]">{t('cookies.categories.analytics.title')}</p>
                <p className="text-sm text-[#5A6B7A]">{t('cookies.categories.analytics.description')}</p>
              </div>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="w-4 h-4 mt-1 rounded border-[#DBE2EA] text-[#0880AE] focus:ring-[#0880AE] focus:ring-2"
              />
            </label>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
          <div className="sm:mr-auto">
            <LanguageSelector />
          </div>
          {!showDetails && (

            <button
              onClick={() => setShowDetails(true)}
              className="text-[#0880AE] hover:bg-[#EBF4F8] font-medium px-4 py-2 rounded-md transition-colors duration-200"
            >
              {t('cookies.banner.customize')}
            </button>
          )}
          <button
            onClick={rejectAll}
            className="bg-white border-2 border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
          >
            {t('cookies.banner.rejectAll')}
          </button>
          {showDetails && (
            <button
              onClick={saveChoices}
              className="bg-[#37699E] hover:bg-[#2E5782] text-white font-semibold px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
            >
              {t('cookies.banner.save')}
            </button>
          )}
          <button
            onClick={acceptAll}
            className="bg-[#FFBF07] hover:bg-[#E5AB06] active:bg-[#CC9805] text-[#2D3748] font-semibold px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
          >
            {t('cookies.banner.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
